import { createBigDecimal, plus, times } from '@0xtorch/big-decimal'
import type { Transfer } from '@0xtorch/core'
import type { BorrowSubCategory, Transaction } from '../../types/transaction'
import { createAssetKey } from '../../utils'
import type {
  CreateCategoryTransactionParameters,
  CreateCategoryTransactionReturnTypes,
} from './types'

type CreateBorrowTransactionParameters = CreateCategoryTransactionParameters & {
  readonly subCategory?: BorrowSubCategory
  readonly transfer: Transfer
}

type CreateBorrowTransactionReturnTypes = CreateCategoryTransactionReturnTypes

export const createBorrowTransaction = ({
  actionId,
  timestamp,
  subCategory,
  transfer,
  assetBalances,
  assetBorrows,
  usedBalances,
}: CreateBorrowTransactionParameters): CreateBorrowTransactionReturnTypes => {
  const key = createAssetKey(transfer.asset)
  const price = transfer.price ?? createBigDecimal(0n)
  const value = times(transfer.amount, price, 6)

  // Add borrow to asset borrow (FIFO)
  assetBorrows.set(key, [
    ...(assetBorrows.get(key) ?? []),
    {
      amount: transfer.amount,
      value,
      borrowedAmount: transfer.amount,
      borrowedValue: value,
    },
  ])

  const balance = (assetBalances.get(key) ?? []).reduce(
    (acc, assetBalance) => plus(acc, assetBalance.amount),
    createBigDecimal(0n),
  )
  const borrowing = (assetBorrows.get(key) ?? []).reduce(
    (acc, assetBorrow) => plus(acc, assetBorrow.amount),
    createBigDecimal(0n),
  )

  const transaction: Transaction = {
    actionId,
    order: 0,
    timestamp,
    category: 'borrow',
    subCategory,
    ins: [
      {
        amount: transfer.amount,
        asset: transfer.asset,
        price,
        zeroCompleted: transfer.price === undefined,
        cost: value,
        balance,
        borrowing,
        feePl: createBigDecimal(0n),
        incomePl: createBigDecimal(0n),
        tradePl: createBigDecimal(0n),
        totalPl: createBigDecimal(0n),
      },
    ],
    outs: [],
  }

  return {
    transaction,
    assetBalances,
    assetBorrows,
    usedBalances,
  }
}
